import React from 'react';

const YieldCurveChart = ({ data }) => {
  const yieldMetrics = [
    { id: 'yield1m', label: '1M', dataKey: 'yield1m' },
    { id: 'yield3m', label: '3M', dataKey: 'yield3m' },
    { id: 'yield6m', label: '6M', dataKey: 'yield6m' },
    { id: 'yield2y', label: '2Y', dataKey: 'yield2y' },
    { id: 'yield10y', label: '10Y', dataKey: 'yield10y' },
    { id: 'yield30y', label: '30Y', dataKey: 'yield30y' }
  ];

  const getYield = (key) => {
    const raw = data?.[key]?.value;
    return typeof raw === 'number' ? raw : null;
  };

  const y2 = getYield('yield2y');
  const y10 = getYield('yield10y');

  const spread = y2 !== null && y10 !== null ? y2 - y10 : null;
  const isInverted = spread !== null && spread > 0;        // short > long
  const isFlat = spread !== null && !isInverted && Math.abs(spread) < 0.15;

  const statusLabel = (() => {
    if (spread === null) return 'No data';
    if (isInverted) return 'Inverted (Short > Long)';
    if (isFlat) return 'Flat (Similar)';
    return 'Normal / Steep (Long > Short)';
  })();

  const explainerText = (() => {
    if (spread === null) {
      return 'Once both 2Y and 10Y yields are available, this panel will describe the curve shape.';
    }
    if (isInverted) {
      return 'Short-term yields are higher than long-term yields. This inverted curve often signals economic stress and higher recession risk.';
    }
    if (isFlat) {
      return 'Short and long yields are very close. A flat curve can mean investors are uncertain about future growth and policy.';
    }
    return 'Long-term yields are above short-term yields. This usually points to healthier growth expectations and a more normal curve.';
  })();

  // For bar heights
  const yieldValues = yieldMetrics
    .map(m => getYield(m.dataKey))
    .filter(v => v !== null);

  const maxYield = yieldValues.length ? Math.max(...yieldValues) : 5;
  const minYield = yieldValues.length ? Math.min(...yieldValues) : 0;

  const getBarHeight = (value) => {
    if (value === null) return 20;
    const range = maxYield - minYield || 1;
    const normalized = ((value - minYield) / range) * 120 + 25;
    return Math.min(Math.max(normalized, 25), 150);
  };

  const formatPct = (value) =>
    value === null ? '—' : `${value.toFixed(2)}%`;

  const formatSpread = (value) =>
    value === null ? '—' : `${value.toFixed(2)}%`;

  return (
    <div className="yield-curve-section">
      {/* Left: bar chart */}
      <div className="chart-card">
        <div className="chart-title">Yield Curve Snapshot</div>
        <div className="yield-chart-container">
          <div className="yield-chart-bars">
            {yieldMetrics.map(metric => {
              const value = getYield(metric.dataKey);
              const height = getBarHeight(value);

              return (
                <div key={metric.id} className="yield-bar-container">
                  <div
                    className="yield-bar"
                    style={{ height: `${height}px` }}
                  />
                  <div className="yield-label">{metric.label}</div>
                  <div className="yield-value">
                    {formatPct(value)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Right: curve signal */}
      <div className="chart-card curve-signal-card">
        <div className="curve-header-row">
          <h3 className="curve-title">Curve Signal (2Y vs 10Y)</h3>
          <span className={`curve-chip ${
            isInverted ? 'curve-chip-inverted'
            : isFlat ? 'curve-chip-flat'
            : 'curve-chip-normal'
          }`}>
            {statusLabel}
          </span>
        </div>

        <div className="curve-spread-row">
          <span className="curve-spread-label">Spread 2Y minus 10Y</span>
          <span className={`curve-spread-value ${
            spread === null
              ? ''
              : spread > 0
                ? 'curve-spread-negative'
                : 'curve-spread-positive'
          }`}>
            {formatSpread(spread)}
          </span>
        </div>

        <p className="curve-description">
          {explainerText}
        </p>

        <div className="curve-levels">
          <div className="curve-level">
            <div className="curve-level-label">Short term (2Y)</div>
            <div className="curve-level-value">
              {formatPct(y2)}
            </div>
          </div>
          <div className="curve-level-divider">
            <span className="curve-arrow">
              {spread !== null && spread > 0 ? '↑ Short > Long' : '↓ Long > Short'}
            </span>
          </div>
          <div className="curve-level">
            <div className="curve-level-label">Long term (10Y)</div>
            <div className="curve-level-value">
              {formatPct(y10)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default YieldCurveChart;
